<template>
  <div class="quiz-container">

    <!-- cabeçalho com logo + título -->
    <div class="quiz-header">
      <div class="icon-wrapper">
        <img :src="logo" alt="Cavalo Correndo" class="quiz-logo" />
      </div>
      <h3 v-if="title" class="quiz-title">{{ title }}</h3>
    </div>

    <!-- Seletor de tema -->
    <div class="mb-3">
      <label for="topic" class="form-label">Escolha um tema:</label>
      <select id="topic" v-model="topic" class="form-select" :disabled="loading || submitted">
        <option value="Hipismo em geral">Hipismo em geral</option>
        <option value="Técnicas de ensino">Técnicas de ensino</option>
        <option value="Técnicas de salto">Técnicas de salto</option>
        <option value="História">A história do hipismo</option>
        <option value="Outro assunto">Escolha o assunto</option>
      </select>
    </div>

    <!-- Campo para tema customizado -->
    <div v-if="topic === 'Outro assunto'" class="mb-3">
      <label for="customTopic" class="form-label">Informe o tema:</label>
      <input
        id="customTopic"
        v-model="customTopic"
        type="text"
        class="form-control"
        placeholder="Digite o tema desejado"
        :disabled="loading || submitted"
      />
    </div>

    <!-- Seletor de nível de dificuldade -->
    <div class="mb-3">
      <label for="difficulty" class="form-label">Nível de dificuldade:</label>
      <select id="difficulty" v-model="difficulty" class="form-select" :disabled="loading || submitted">
        <option value="Iniciantes">Iniciantes</option>
        <option value="Amadores">Amadores</option>
        <option value="Profissionais">Profissionais</option>
      </select>
    </div>

    <!-- Botão para iniciar geração do quiz: só mostra se NÃO finalizou (submitted=false) -->
    <div v-if="!submitted" class="mb-3 text-center">
      <button
        @click="loadQuiz"
        class="btn btn-primary w-100 fs-6"
        :disabled="loading || (topic === 'Outro assunto' && !customTopic)"
      >
        {{ loading ? 'Gerando quiz...' : 'Iniciar Quiz' }}
      </button>
    </div>

    <div v-if="error" class="alert alert-danger text-center">
      {{ error }}
    </div>

    <div v-if="questions.length">
      <div v-for="(q, idx) in questions" :key="idx" class="quiz-question mb-4">
        <h6 class="question-text">Q{{ idx + 1 }}: {{ q.question }}</h6>
        <ul class="choices-list list-unstyled">
          <li
            v-for="(choice, cIdx) in q.choices"
            :key="cIdx"
            :class="{
              'text-success': submitted && cIdx === q.correctIndex,
              'text-danger': submitted && answers[idx] === cIdx && cIdx !== q.correctIndex
            }"
            class="choice-item"
          >
            <label>
              <input
                type="radio"
                :name="`q-${idx}`"
                :value="cIdx"
                v-model.number="answers[idx]"
                class="form-check-input me-2"
                :disabled="submitted"
              />
              {{ choice }}
            </label>
          </li>
        </ul>
        <div v-if="submitted && answers[idx] !== q.correctIndex" class="correct-answer">
          Resposta correta: <strong>{{ q.choices[q.correctIndex] }}</strong>
          <p class="explanation">{{ q.explanation }}</p>
        </div>
      </div>

      <div class="d-flex flex-column gap-2 mb-3">
        <!-- Enviar respostas só aparece se NÃO finalizou -->
        <button
          v-if="!submitted"
          @click="submitQuiz"
          class="btn btn-success w-100"
        >
          Enviar respostas
        </button>

        <!-- Reiniciar sempre disponível -->
        <button
          @click="resetQuiz"
          class="btn btn-secondary w-100 fs-6"
          :disabled="loading"
        >
          Reiniciar
        </button>
      </div>

      <div v-if="submitted" class="quiz-score mt-3 text-center">
        <h6>Você acertou {{ score }} de {{ questions.length }}.</h6>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from '@/services/axios';
import logo from '@/assets/horse.png';    // <— importe sua imagem aqui

const props = defineProps({
  numQuestions: { type: Number, default: 4 },
  title:        { type: String, default: 'Vamos fazer um Quiz?' }
});

// Estado do componente
const topic = ref('Hipismo em geral');
const customTopic = ref('');
const difficulty = ref('Iniciantes');
const questions = ref([]);
const answers = ref([]);
const loading = ref(false);
const submitted = ref(false);
const score = ref(0);
const error = ref('');

// Carrega quiz conforme tema, dificuldade e número de perguntas
async function loadQuiz() {
  loading.value = true;
  error.value = '';
  submitted.value = false;
  score.value = 0;
  try {
    const response = await axios.post('/api/quiz/generate', {
      numQuestions: props.numQuestions,
      difficulty: difficulty.value,
      topic: topic.value === 'Outro assunto' ? customTopic.value : topic.value
    });
    questions.value = response.data.quiz;
    answers.value = Array(questions.value.length).fill(null);
  } catch (err) {
    console.error('Erro ao carregar quiz:', err);
    error.value = err.response?.data?.error || 'Não foi possível gerar o quiz.';
  } finally {
    loading.value = false;
  }
}

// Calcula score e marca submissão
function submitQuiz() {
  score.value = questions.value.reduce((acc, q, idx) => acc + (answers.value[idx] === q.correctIndex ? 1 : 0), 0);
  submitted.value = true;
}

// Reinicia estado do componente
function resetQuiz() {
  questions.value = [];
  answers.value = [];
  submitted.value = false;
  score.value = 0;
  error.value = '';
}
</script>

<style scoped>
.quiz-container {
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 1rem;
}

/* Cabeçalho flexível */
.quiz-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
@media (max-width: 520px) {
  .quiz-header {
    flex-direction: column;
  }
}

/* Logo responsivo */
.quiz-logo {
  height: 120px;
  width: auto;
  border-radius: 50%;    /* cantos arredondados */
}
@media (max-width: 520px) {
  .quiz-logo {
    height: 200px;
  }
}

/* Título responsivo */
.quiz-title {
  margin: 0;
  font-size: 1.5rem;
}
@media (max-width: 520px) {
  .quiz-title {
    font-size: 1.25rem;
    text-align: center;
  }
}

/* Formulários e botões full-width */
.form-select,
.form-control,
.btn {
  width: 100%;
  margin-bottom: 0.75rem;
}

/* Espaço extra entre pergunta e lista de respostas */
.question-text {
  margin-bottom: 1rem;  /* era algo como 0.5rem; deixe 1rem para “abrir” mais */
}

/* Lista flexível com gap para controlar espaçamento entre itens */
.choices-list {
  display: flex;
  flex-direction: column;
  gap: 0rem;         /* diminui o espaçamento entre as linhas de resposta */
  margin: 0;            /* zera margens padrão de <ul> */
  padding: 0;           /* zera paddings padrão de <ul> */
}

/* Itens de resposta com padding menor */
.choice-item {
  padding: 0.3rem 0.5rem;  /* mais enxuto que antes */
  margin: 0;               /* remova margin-bottom se existir */
  line-height: 1.3;        /* texto legível, porém compacto */
}

/* destaque de hover */
.choice-item:hover {
  background: rgba(0,0,0,0.03);
}

/* fundo verde para acertos */
.choice-item.text-success {
  background-color: #d1e7dd;
  border: 1px solid #badbcc;
}
.choice-item.text-success label {
  color: #0f5132;
}

/* fundo vermelho para erros */
.choice-item.text-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
}
.choice-item.text-danger label {
  color: #842029;
}
/* Alinha o texto das opções em múltiplas linhas */
.choice-item label {
  display: flex;
  align-items: flex-start; /* alinha o topo do texto com o rádio */
  gap: 0.5rem;             /* espaço entre o input e o texto */
}

/* Ajuste vertical fino para centrar o rádio no início do texto */
.choice-item label input[type="radio"] {
  margin-top: 0.25rem;     /* sobe ou desce conforme o alinhamento desejado */
}


/* Score e feedback */
.quiz-score h6 {
  font-size: 1.25rem;
}

/* Ajuste geral de fontes para legibilidade */
body, .quiz-container {
  font-size: 1rem;
  line-height: 1.5;
}
.correct-answer .explanation {
  margin-top: 0.5rem;
  font-style: italic;
  color: #333;
}

/* Container arredondado tipo “ícone” */
.icon-wrapper {
  width: 200px;
  height: 200px;
  background: #fff;
  border-radius: 15px;               /* cantos do “ícone” */
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2); /* sombra suave */
  margin-bottom: 0.5rem;
}

/* Imagem circular dentro do ícone */
.quiz-logo {
  width: 85%;
  height: 85%;
  object-fit: cover;
  border-radius: 50%;                /* círculo interno */
}

</style>
